<template>
	<div>
		<section
			class="text-center bg-gradient-to-b from-color-main to-[#91469d] rounded-b-lg"
			:class="[heroHeight, $isMobile.any ? ' mb-10 pt-10' : ' mb-20 pt-20']"
		>
			<h1 class="text-white text-4xl" v-html="content.heroSection.headline" />
			<h2 class="text-white text-lg mb-14" v-html="content.heroSection.subHeadline" />

			<section
				class="absolute top-60 left-0 right-0"
				:class="[
					!quizStartedOrFinished ? 'block' : 'hidden',
					{ ' container max-w-4xl ': !$isMobile.any }
				]"
			>
				<div class="mx-2 pb-10 bg-white rounded-lg">
					<div class="flex space-x-3 items-center mb-4 pt-4 pl-4">
						<HokIcon name="icon:lightbulb" color="main" />
						<p class="text-main font-bold mb-0 leading-none">
							{{ content.quizBox.title }}
						</p>
					</div>
					<div class="flex flex-col items-center">
						<NuxtImg
							src="/img/b2c-lps/jobinterviewTraining/jobquiz-illustration.svg"
							ssr
							class="mb-4 aspect-square"
						/>
						<div class="px-10 text-start">
							<p class="mb-2">
								{{ content.quizBox.headline }}
							</p>
							<p>{{ content.quizBox.text }}</p>
						</div>
						<HokButton color="main" @click="startQuiz"> Quiz starten</HokButton>
					</div>
				</div>
			</section>
			<section
				class="absolute top-60 left-0 right-0"
				:class="[
					quizStartedOrFinished ? 'block' : 'hidden',
					{ 'max-w-4xl container': !$isMobile.any }
				]"
			>
				<JobQuiz
					id="quiz"
					:quiz-started-or-finished="quizStartedOrFinished"
					:show-quiz-start="showQuizStart"
					:questions="questions"
					:show-quiz="showQuiz"
					:show-results="showResults"
					:quiz-results="results"
					:quiz-id="quizId"
					:quiz-texts="content"
					:articles="articles"
					:shuffled-answers="shuffledAnswers"
					:yes-or-no-quiz="yesOrNoQuiz"
					:weighted-quiz="weightedQuiz"
					:is-pwa="false"
					@start-quiz="startQuiz"
					@finished="finishQuiz($event)"
					@reset="resetQuiz"
					@track-question="trackQuestion($event)"
					@track-redirect="trackRedirect($event)"
				/>
			</section>
		</section>
		<section :class="$isMobile.any ? 'px-8 py-10' : 'py-20'">
			<div class="flex flex-col items-center">
				<h2 class="text-text text-4xl font-bold">{{ content.infoSection.headline }}</h2>
				<h3 class="text-text text-lg mb-12">
					{{ content.infoSection.subHeadline }}
				</h3>
				<div class="mb-5" :class="{ 'flex max-w-6xl': !$isMobile.any }">
					<div
						v-for="linkBox in content.infoSection.linkBoxes"
						:key="linkBox.link"
						class="rounded-xl shadow-xl flex flex-col items-center text-center px-8 mx-6"
						:class="!$isMobile.any ? 'w-1/3' : 'mb-5'"
					>
						<HokIcon :name="linkBox.icon" :size="14" color="main" class="my-9"></HokIcon>
						<p class="font-bold mb-1">{{ linkBox.headline }}</p>
						<p class="h-12">{{ linkBox.text }}</p>
						<HokLink :to="linkBox.link" class="mb-12">Mehr erfahren ></HokLink>
					</div>
				</div>
			</div>
		</section>
		<section class="bg-white" :class="$isMobile.any ? 'px-8 py-10' : 'py-20'">
			<div
				class="flex flex-col items-center max-w-6xl pb-20 mx-auto"
				:class="{
					'flex flex-col items-center bg-grey-background  rounded-lg ': !$isMobile.any
				}"
			>
				<h2
					class="text-text text-2xl mt-12 font-bold"
					:class="$isMobile.any ? 'text-start mb-6' : 'mb-12'"
				>
					{{ content.howToSection.headline }}
				</h2>
				<div class="mb-5" :class="{ 'flex justify-between space-x-2 mx-36': !$isMobile.any }">
					<p
						v-if="content.howToSection && content.howToSection.textLeft"
						:class="{ 'w-1/2': !$isMobile.any }"
						v-html="content.howToSection.textLeft"
					/>
					<div
						v-if="content.howToSection && content.howToSection.textRight"
						:class="{ 'w-1/2': !$isMobile.any }"
						v-html="content.howToSection.textRight"
					/>
					<div
						v-if="content.howToSection && content.howToSection.longText"
						v-html="content.howToSection.longText"
					></div>
				</div>
			</div>
		</section>
		<section class="pb-20">
			<div class="flex flex-col items-center container max-w-6xl overflow-auto">
				<ContentMediaSplitComponent class="mb-20" :content="content.firstLinkCard" />
				<div :class="{ 'flex justify-between': !$isMobile.any }" class="mb-20">
					<div
						v-for="(article, index) in articles"
						:key="article._id"
						class="py-2"
						:class="{
							'w-1/3': !$isMobile.any,
							'pl-2': !$isMobile.any && index % 3 !== 0,
							'pr-2': !$isMobile.any && index % 3 !== 2
						}"
					>
						<ArticleCard :article="article" />
					</div>
				</div>

				<ContentMediaSplitComponent class="mb-20" :content="content.secondLinkCard" />
			</div>
		</section>
	</div>
</template>

<script lang="ts">
import { defineComponent, nextTick, type PropType } from 'vue';
import JobQuiz from '@hokify/shared-components-nuxt3/lib/components/JobQuiz.vue';
import type { IAPIArticle } from '@hokify/magazine-interface';
import { mapStores } from 'pinia';
import ContentMediaSplitComponent from '~/components/website/ContentMediaSplitComponent.vue';
import { useQuizStore } from '@/stores/quiz';
import ArticleCard from '~/components/website/ArticleCard.vue';

export default defineComponent({
	name: 'QuizLandingPage',
	layout: 'default',
	components: { ContentMediaSplitComponent, ArticleCard, JobQuiz },
	computed: {
		...mapStores(useQuizStore),
		quizStartedOrFinished(): boolean {
			return this.showQuiz || this.showResults;
		},
		heroHeight(): string {
			if (this.yesOrNoQuiz) {
				if (this.$isMobile.any) {
					return 'min-h-[800px]';
				}
				return 'min-h-[700px]';
			}
			if (this.weightedQuiz && !this.hasExplanation) {
				if (this.$isMobile.any) {
					return 'min-h-[1000px]';
				}
				return 'min-h-[800px]';
			}

			if (this.$isMobile.any) {
				return 'min-h-[1250px]';
			}

			return 'min-h-[900px]';
		}
	},
	data() {
		return {
			showQuiz: false,
			showResults: false,
			showQuizStart: false
		};
	},
	mounted() {
		this.showQuizStart = true;
	},
	methods: {
		startQuiz() {
			this.showQuiz = true;
			this.showResults = false;
			if (this.$isMobile.any) {
				nextTick(() => {
					this.$scrollTo('#quiz', { offset: -20 });
				});
			}
			this.$trackWebsiteEvent('quiz_start', {});
		},
		async finishQuiz(answers?: { questionId: string; answerId: string }[]) {
			this.showQuiz = false;
			this.showResults = true;
			if (answers) {
				try {
					await this.quizStore.sendAnsweredQuestions(answers);
				} catch (err: any) {
					this.$nuxt.$errorHandler?.call(this, err);
				}
			}
		},
		resetQuiz() {
			this.showQuiz = true;
			this.showResults = false;
		},
		trackQuestion(question: number) {
			this.$trackWebsiteEvent('quiz_finish_question', { index: question });
		},
		trackRedirect(redirect: 'login' | 'jobs' | 'magazine') {
			this.$trackWebsiteEvent('quiz_finished_redirect', { redirect });
		}
	},
	props: {
		questions: {
			type: Array as PropType<
				{
					_id: string;
					question: string;
					options: {
						correct: boolean;
						explanation: string;
						option: string;
						totalAnswers: number;
					}[];
				}[]
			>,
			required: true
		},
		results: {
			type: Array as PropType<{ title: string; text: string }[]>,
			required: false,
			default: () => []
		},
		quizId: { type: String, required: false, default: '' },
		yesOrNoQuiz: { type: Boolean, default: false },
		weightedQuiz: { type: Boolean, default: false },
		shuffledAnswers: { type: Boolean, default: true },
		hasExplanation: { type: Boolean, default: true },
		articles: { type: Array as PropType<IAPIArticle[]>, required: true },
		content: { type: Object, required: true }
	}
});
</script>
